import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  AdminTrainingEmptyStateIcon,
  CircleRightArrowIcon,
} from 'secondstep-components'
import { PUSH } from 'layers/navigation/store/types'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { isFlagOn } from 'utils/featureFlags'
import { TOOLTIP_LOCATIONS, TOOLTIP_PROGRAMS } from 'utils/tooltipHelpers'
import { formatLastUpdatedRelativeTime } from 'utils/reportsHelpers'
import AccessibleTextLink from 'components/AccessibleTextLink'
import AdminDonutChart from 'components/AdminDonutChart'
import AdminPathwayProgress from 'components/AdminPathwayProgress'
import {
  ChartsWrapper,
  EmptyStateMessage,
  EmptyStateWrapper,
  HeaderWrapper,
  LastUpdatedMessage,
  LeftBox,
  MainTitle,
  ModalBodyCopy,
  ModalCloseButton,
  ModalHeader,
  MonitorSnapshotWrapper,
  OverallProgressHeaderWrapper,
  OverallProgressTitle,
  OverallProgressWrapper,
  PathwayAndProgressWrapper,
  RightBox,
  StyledAdminExportDownloadButton,
  StyledAdminTooltip,
  StyledBodyCopy,
  StyledClosableModal,
  StyledDonutHairline,
  StyledHairline,
  UserProgressButton,
  UserProgressButtonTitle,
} from './component.styles'
import {
  EMPTY_STATE_MESSAGE,
  MAIN_DESCRIPTION_TEXT,
  MAIN_TITLE_TEXT,
  MODAL_CLOSE_BUTTON_TEXT,
  MODAL_LINK_TEXT,
  OVERALL_PROGRESS_TITLE_TEXT,
  PROGRAM_NAME_EDUCATOR_PRACTICES,
  PROGRAM_NAME_SCHOOLWIDE_PRACTICES,
  PROGRAM_NAME_STUDENT_ACTIVITIES,
  USER_PROGRESS_BUTTON_TITLE,
  USER_PROGRESS_EDUCATOR_PRACTICES_URL,
} from './constants'

const AdminHsMonitorSnapshot = ({
  isMultiSite,
  lastUpdatedAt,
  modalText,
  modalTitle,
  navigationEvent,
  site,
}) => {
  const [showModal, setShowModal] = useState(false)
  const {
    educatorPracticesPathways,
    preferences,
    schoolwidePracticesPathways,
    siteId,
    studentActivitiesByGrade,
  } = site || {}
  const { studentActivities: isOptedInStudentActivties } = preferences || {}
  const lastUpdatedMessage = formatLastUpdatedRelativeTime(lastUpdatedAt)
  const pacingStartDates = Object.values(site?.pathwayDates || {})
    .map(({ startDateInMs }) => startDateInMs)
    .filter(Number.isFinite)
  const firstPacingStartDate = Math.min(...pacingStartDates)
  const isBeforeFirstPacingStartDate =
    pacingStartDates.length > 0 && Date.now() < firstPacingStartDate
  const isPacingPathway = site?.currentPacingName?.includes('Pathway')
  const isProgressMade =
    educatorPracticesPathways?.totalPracticesOrActivitiesCompleted > 0 ||
    schoolwidePracticesPathways?.totalPracticesOrActivitiesCompleted > 0 ||
    studentActivitiesByGrade?.totalPracticesOrActivitiesCompleted > 0
  const simplifyHsSetup = isFlagOn(['feature_LEARN-29656-Simplify-HS-Setup'])
  const showEmptyState = simplifyHsSetup
    ? isBeforeFirstPacingStartDate
    : !isPacingPathway && !isProgressMade

  return (
    <MonitorSnapshotWrapper isMultiSite={isMultiSite}>
      <HeaderWrapper isMultiSite={isMultiSite}>
        <LeftBox>
          <MainTitle>{MAIN_TITLE_TEXT}</MainTitle>
          <StyledBodyCopy>{MAIN_DESCRIPTION_TEXT}</StyledBodyCopy>
        </LeftBox>
        <RightBox>
          <LastUpdatedMessage>{lastUpdatedMessage}</LastUpdatedMessage>
          <StyledAdminExportDownloadButton
            programKey={TOOLTIP_PROGRAMS.HS}
            toggleView={TOGGLE_VIEW.MONITOR}
            tooltipLocation={TOOLTIP_LOCATIONS.EXPORT_BUTTON}
          />
        </RightBox>
      </HeaderWrapper>
      {showEmptyState && (
        <EmptyStateWrapper isMultiSite={isMultiSite}>
          <AdminTrainingEmptyStateIcon />
          <EmptyStateMessage>{EMPTY_STATE_MESSAGE}</EmptyStateMessage>
        </EmptyStateWrapper>
      )}
      {!showEmptyState && (
        <PathwayAndProgressWrapper>
          <OverallProgressWrapper isMultiSite={isMultiSite}>
            <OverallProgressHeaderWrapper>
              <OverallProgressTitle>
                {OVERALL_PROGRESS_TITLE_TEXT}
              </OverallProgressTitle>
              <StyledAdminTooltip
                location={TOOLTIP_LOCATIONS.OVERALL_PROGRESS}
                programKey={TOOLTIP_PROGRAMS.HS_MONITOR}
              />
              <AccessibleTextLink
                ariaLabel="how-are-these-calculated-modal-link"
                dataTestId="how-are-these-calculated-modal-link"
                onClick={() => setShowModal(true)}
              >
                {MODAL_LINK_TEXT}
              </AccessibleTextLink>
            </OverallProgressHeaderWrapper>
            <StyledHairline />
            <ChartsWrapper>
              <AdminDonutChart
                data={schoolwidePracticesPathways}
                programName={PROGRAM_NAME_SCHOOLWIDE_PRACTICES}
                siteId={siteId}
              />
              <StyledDonutHairline />
              <AdminDonutChart
                data={educatorPracticesPathways}
                programName={PROGRAM_NAME_EDUCATOR_PRACTICES}
                siteId={siteId}
              />
              <StyledDonutHairline />
              <AdminDonutChart
                data={studentActivitiesByGrade}
                isOptedInStudentActivties={isOptedInStudentActivties}
                programName={PROGRAM_NAME_STUDENT_ACTIVITIES}
                siteId={siteId}
              />
            </ChartsWrapper>
          </OverallProgressWrapper>
          <AdminPathwayProgress siteId={siteId} />
        </PathwayAndProgressWrapper>
      )}
      <UserProgressButton
        onClick={() =>
          navigationEvent(
            `${USER_PROGRESS_EDUCATOR_PRACTICES_URL}?siteId=${siteId}`,
            PUSH,
          )
        }
      >
        <UserProgressButtonTitle>
          {USER_PROGRESS_BUTTON_TITLE}
        </UserProgressButtonTitle>
        <CircleRightArrowIcon />
      </UserProgressButton>
      <StyledClosableModal
        onClose={() => setShowModal(false)}
        show={showModal}
        showClose
      >
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalBodyCopy>{modalText}</ModalBodyCopy>
        <ModalCloseButton onClick={() => setShowModal(false)}>
          {MODAL_CLOSE_BUTTON_TEXT}
        </ModalCloseButton>
      </StyledClosableModal>
    </MonitorSnapshotWrapper>
  )
}

AdminHsMonitorSnapshot.propTypes = {
  isMultiSite: PropTypes.bool,
  lastUpdatedAt: PropTypes.string,
  modalText: PropTypes.string,
  modalTitle: PropTypes.string,
  navigationEvent: PropTypes.func,
  site: PropTypes.object, // TODO: define props needed for this component
}

export default AdminHsMonitorSnapshot
